import type { CompanyLinkView } from "@portal/schemas";
import { cn, Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from "@portal/ui";
import { ChevronDown, ExternalLink, Folder, FolderOpen, Globe } from "lucide-react";
import { useState } from "react";
import { useCompanyLinks } from "../api/queries";
import { CardStatus } from "./cards/CardStatus";

/** カテゴリごとにまとめる(API はカテゴリ → 並び順で返す)。 */
export function groupLinksByCategory(
  links: readonly CompanyLinkView[],
): { category: string; links: CompanyLinkView[] }[] {
  const groups = new Map<string, CompanyLinkView[]>();
  for (const l of links) {
    const g = groups.get(l.category) ?? [];
    g.push(l);
    groups.set(l.category, g);
  }
  return [...groups].map(([category, links]) => ({ category, links }));
}

/** リンクの favicon の取得先(ブックマークとプロジェクトの外部リンクで共通)。サーバーが取得して保存したもの(取得日時を ?v= に付け、取り直したら読み直す)。 */
export const faviconSrc = (link: Pick<CompanyLinkView, "id" | "favicon">): string | null =>
  link.favicon
    ? `/api/favicons/${encodeURIComponent(link.id)}?v=${encodeURIComponent(link.favicon)}`
    : null;

/**
 * リンクの favicon。サーバーが取得して保存した画像を、ポータルから読む(外のサイトへは通信しない。
 * ユーザー決定 2026-10-07)。まだ取れていない・読めないときは、地球の絵にする。
 */
function LinkFavicon({ link }: { link: CompanyLinkView }) {
  const src = faviconSrc(link);
  const [failed, setFailed] = useState(false);
  if (!src || failed)
    return <Globe aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />;
  return (
    <img
      src={src}
      alt=""
      width={16}
      height={16}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-4 shrink-0"
    />
  );
}

/**
 * ホームの最上段に置く、ブックマークバー(SPEC §13.4。ユーザー要望 2026-10-07)。
 * Chrome のブックマークバーのように 1 行に並べ、カテゴリは「フォルダ」として、押すとメニューでリンクが開く。
 * リンクは新規タブで開く。リンクが 1 件もなければ何も出さない。
 */
export function BookmarkBar({ className }: { className?: string }) {
  const links = useCompanyLinks();
  const groups = links.data ? groupLinksByCategory(links.data.links) : [];
  if (links.data && groups.length === 0) return null;
  return (
    <nav
      aria-label="ブックマーク"
      data-testid="company-links-card"
      // 下の区切り線にうっすら影を落として、ヘッダーとの区切りを分かりやすくする(ユーザー要望 2026-10-07。ここだけ)
      className={cn(
        "flex shrink-0 items-center gap-0.5 overflow-x-auto border-b px-6 py-1 shadow-[0_2px_3px_-1px_rgb(0_0_0/0.14)]",
        className,
      )}
    >
      <CardStatus
        isPending={false} // 読み込み中は何も出さない(なければバーごと消えるため、出し入れでずれる)
        isError={links.isError}
        refetch={() => links.refetch()}
      />
      {/* Menubar: 開いている間に、となりのフォルダへ移ると(ホバーでも 1 回のクリックでも)すぐ切り替わる */}
      <Menubar className="gap-0.5 border-0 bg-transparent p-0">
        {groups.map((g) => (
          <MenubarMenu key={g.category}>
            {/* 開いているフォルダは、背景色・開いたフォルダの絵・上向きの矢印で分かるようにする(2026-10-07) */}
            <MenubarTrigger className="group h-7 shrink-0 gap-1.5 px-2 data-[state=open]:font-semibold">
              <Folder
                aria-hidden="true"
                className="size-4 text-muted-foreground group-data-[state=open]:hidden"
              />
              <FolderOpen
                aria-hidden="true"
                className="hidden size-4 text-foreground group-data-[state=open]:block"
              />
              {g.category}
              <ChevronDown
                aria-hidden="true"
                className="size-3.5 opacity-60 group-data-[state=open]:rotate-180"
              />
            </MenubarTrigger>
            <MenubarContent className="min-w-56">
              {g.links.map((l) => (
                <MenubarItem key={l.id} asChild>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2"
                  >
                    <span className="mt-0.5">
                      <LinkFavicon link={l} />
                    </span>
                    <span className="flex flex-col">
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        {l.title}
                        <ExternalLink aria-hidden="true" className="size-3 text-muted-foreground" />
                      </span>
                      {/* リンク先も見えるようにする(ユーザー指示 2026-09-14) */}
                      <span className="max-w-72 truncate text-xs text-muted-foreground">
                        {l.url.replace(/^https?:\/\//, "")}
                      </span>
                      {l.note && <span className="text-xs text-muted-foreground">{l.note}</span>}
                    </span>
                  </a>
                </MenubarItem>
              ))}
            </MenubarContent>
          </MenubarMenu>
        ))}
      </Menubar>
    </nav>
  );
}
